import { LayoutDashboard, Upload, Search, Truck, MessageSquare, User, Package, Settings, Star, Clock, Building2 } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useUser } from "@/contexts/UserContext";
import { useQuery } from "@tanstack/react-query";

type UserType = "Shipper" | "Carrier" | "Broker";

const allNavigationItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard, testId: "dashboard", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "Post Load", url: "/post-load", icon: Upload, testId: "post-load", roles: ["Shipper", "Broker"] },
  { title: "Find Trucks", url: "/find-trucks", icon: Search, testId: "find-trucks", roles: ["Shipper", "Broker"] },
  { title: "Post Truck Space", url: "/post-truck", icon: Truck, testId: "post-truck", roles: ["Carrier", "Broker"] },
  { title: "Find Loads", url: "/find-loads", icon: Package, testId: "find-loads", roles: ["Carrier", "Broker"] },
  { title: "My Partners", url: "/preferred-partners", icon: Star, testId: "preferred-partners", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "Shipment Workspace", url: "/shipments", icon: MessageSquare, testId: "messages", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "History", url: "/history", icon: Clock, testId: "history", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "Settings", url: "/settings", icon: Settings, testId: "settings", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "Preferences", url: "/preferences", icon: User, testId: "preferences", roles: ["Shipper", "Carrier", "Broker"] },
  { title: "Company Profile", url: "/profile", icon: Building2, testId: "profile", roles: ["Shipper", "Carrier", "Broker"] },
];

const brokerNavOrder = [
  "Dashboard", "Shipment Workspace", "History", "Post Load", "Find Loads",
  "Post Truck Space", "Find Trucks", "My Partners",
  "Settings", "Preferences", "Company Profile"
];

const shipperNavOrder = [
  "Dashboard", "Shipment Workspace", "History", "Post Load", "Find Trucks",
  "My Partners", "Settings", "Preferences", "Company Profile"
];

const carrierNavOrder = [
  "Dashboard", "Shipment Workspace", "History", "Post Truck Space", "Find Loads",
  "My Partners", "Settings", "Preferences", "Company Profile"
];

function UnreadMessagesBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  const label = count >= 10 ? "9+" : String(count);
  return (
    <span
      data-testid="badge-messages-unread"
      className="ml-auto min-w-[18px] h-4 px-1 flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold leading-none flex-shrink-0"
    >
      {label}
    </span>
  );
}

export function AppSidebar() {
  const { userType, isLoading, user } = useUser();

  const { data: unreadData } = useQuery<{ unreadCount: number }>({
    queryKey: ["/api/messages/unread-count"],
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.unreadCount ?? 0;

  const filteredNavigationItems = allNavigationItems.filter(item =>
    item.roles.includes(userType)
  );

  const getNavOrder = () => {
    if (userType === "Broker") return brokerNavOrder;
    if (userType === "Shipper") return shipperNavOrder;
    if (userType === "Carrier") return carrierNavOrder;
    return null;
  };

  const navOrder = getNavOrder();
  const orderedNavigationItems = (navOrder
    ? navOrder
        .map(title => filteredNavigationItems.find(item => item.title === title))
        .filter(Boolean) as typeof filteredNavigationItems
    : filteredNavigationItems
  ).map(item =>
    item.title === "Company Profile" && user?.id
      ? { ...item, url: `/profile/${user.id}` }
      : item
  );

  return (
    <Sidebar>
      <SidebarHeader className="p-4 border-b">
        <a href="/" className="flex items-center gap-3 hover-elevate rounded-md p-1 -m-1" data-testid="link-logo-home">
          <img src={logoImage} alt="Fill The Trucks" className="h-10" />
        </a>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {isLoading ? (
                <>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <SidebarMenuItem key={i}>
                      <div className="flex items-center gap-2 px-2 py-1.5">
                        <Skeleton className="w-4 h-4" />
                        <Skeleton className="w-24 h-4" />
                      </div>
                    </SidebarMenuItem>
                  ))}
                </>
              ) : (
                <>
                  {orderedNavigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild data-testid={`sidebar-${item.testId}`}>
                        <a href={item.url} className="flex items-center w-full gap-2">
                          <item.icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1 truncate">{item.title}</span>
                          {item.title === "Shipment Workspace" && (
                            <UnreadMessagesBadge count={unreadCount} />
                          )}
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
